import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { ContentService } from "../../services/content.service";
import { calculateAge } from "../../utils/utils";
import { environment } from "../../../environments/environment";
import { UserProfileModalComponent } from "../user-profile-modal/user-profile-modal.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DashboardService } from "../../services/dashboard.service";
import { AuthService } from "../../services/auth.service";
import { of } from "rxjs";
import { catchError } from "rxjs/operators";
import { QuestionnaireService } from "../../services/questionnaire.service";

@Component({
  selector: "app-donor-table",
  templateUrl: "./donor-table.component.html",
  styleUrls: ["./donor-table.component.scss"],
})
export class DonorTableComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() donors: any[] = [];
  @Input() admins: any[] = [];
  @Input() activeCardFilter: string | null = null;
  @Input() incomingFilters: Record<string, string[]> = {};
  @Output() clearFiltersEvent = new EventEmitter<string | null>();
  hoveredDonor: string | null = null;
  filteredDonors: any[] = [];
  searchQuery = "";
  private metricsCache = new Map<string, any[]>();
  private loadingMetrics = new Set<string>();
  private motivationTranslationCache = new Map<string, string>();
  private loadingMotivationTranslations = new Set<string>();
  isBulkMetricsLoading = false;
  private useBulkMetrics = true;

  isLoadingRecommendations = false;
  isLoadingDonors = false;

  selectedRecipient = false;
  isSortByRecommendationApplied = false;
  recommendationScores = new Map<string, number>();
  donorRecommendations = new Map<string, any>();

  showExpandedMetrics = false;

  @ViewChild("scrollWrapper") scrollWrapper!: ElementRef;
  isContentScrollable = false;
  private resizeObserver?: ResizeObserver;
  private readonly handleWindowResize = () => this.checkIfScrollable();

  availableFilters = [
    // {
    //   key: 'donors',
    //   label: 'Donors',
    //   options: [''],
    // },
    {
      key: "recipients",
      label: "Recipients",
      options: [""],
    },
    {
      key: "status",
      label: "Status",
      options: [""],
    },
    {
      key: "assignedTo",
      label: "Assigned To",
      options: [""],
    },
  ];

  activeSubFilter: string | null = null;
  activeFilters: Record<string, string[]> = {};
  filterSearch = "";
  subFilterTopOffset = 0;

  filters: Record<string, Set<string>> = {};
  private pendingIncomingFilters: Record<string, string[]> = {};
  private incomingFiltersApplied = false;

  sortedField: string | null = null;
  progressSortDirection: "asc" | "desc" = "asc";
  assignedSortDirection: "asc" | "desc" = "asc";
  statusSortDirection: "asc" | "desc" = "asc";
  donorSortDirection: "asc" | "desc" = "asc";
  recipientSortDirection: "asc" | "desc" = "asc";
  expandedSortDirection: "asc" | "desc" = "asc";
  donorMetricFlags: Record<string, { red: string[]; yellow: string[] }> = {};

  readonly pageSizeOptions = [5, 10, 15, 25, 100, 0];
  private readonly pageSizeStorageKey = "donorOverviewPageSize";
  pageSize = 5;
  currentPage = 1;
  totalPages = 1;

  constructor(
    private router: Router,
    private contentService: ContentService,
    private dashboardService: DashboardService,
    private questionnaireService: QuestionnaireService,
    private authService: AuthService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.restorePageSize();
    this.pendingIncomingFilters = { ...(this.incomingFilters || {}) };

    this.initializeFromDonors();
  }

  private initializeFromDonors(): void {
    // 1) reset filtered list from donors
    this.filteredDonors = [...this.donors];

    // 2) rebuild dynamic filter options (recipients, status, assignedTo, buckets)
    this.rebuildFilterOptions();

    // 3) keep any existing activeFilters in-bounds with new options
    this.syncActiveFiltersWithOptions?.();

    // 4) re-apply filters and default sorting
    this.applyFilters();
    this.sortByStepsCompleted(); // default sort (high→low)

    // 5) recompute flags & pagination & status values
    this.populateDonorFlags();
    this.updateStatusFilterValues();
    this.updatePagination();
    this.ensureMetricsForAllDonors();
    this.translateSpanishMotivationMetrics();
  }

  isAdmin() {
    return this.authService.isAdmin();
  }

  get paginatedDonors(): any[] {
    const pageSize = this.effectivePageSize;
    const start = (this.currentPage - 1) * pageSize;
    return this.filteredDonors.slice(start, start + pageSize);
  }

  get effectivePageSize(): number {
    return this.pageSize === 0 ? this.filteredDonors.length || 1 : this.pageSize;
  }

  private get isMobilePagination(): boolean {
    return typeof window !== "undefined" && window.innerWidth <= 767;
  }

  get paginationItems(): Array<number | string> {
    if (this.isMobilePagination && this.totalPages > 2) {
      return [];
    }

    if (this.totalPages <= 5) {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    }

    if (this.currentPage <= 3) {
      return [1, 2, 3, "ellipsis", this.totalPages];
    }

    if (this.currentPage >= this.totalPages - 2) {
      return [
        1,
        "ellipsis",
        this.totalPages - 2,
        this.totalPages - 1,
        this.totalPages,
      ];
    }

    return [1, "ellipsis", this.currentPage, "ellipsis", this.totalPages];
  }

  get pageRangeStart(): number {
    if (this.filteredDonors.length === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    return Math.min(
      this.currentPage * this.effectivePageSize,
      this.filteredDonors.length
    );
  }

  setPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.refreshTableLayout(true);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.cachePageSize(pageSize);
    this.currentPage = 1;
    this.updatePagination();
    this.refreshTableLayout(true);
  }

  updatePagination() {
    this.totalPages =
      Math.ceil(this.filteredDonors.length / this.effectivePageSize) || 1;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
    if (this.currentPage < 1) this.currentPage = 1;
    this.refreshTableLayout();
  }

  private restorePageSize(): void {
    if (typeof window === "undefined") {
      return;
    }

    const cachedPageSize = Number(
      window.localStorage.getItem(this.pageSizeStorageKey)
    );

    if (this.pageSizeOptions.includes(cachedPageSize)) {
      this.pageSize = cachedPageSize;
    }
  }

  private cachePageSize(pageSize: number): void {
    if (typeof window === "undefined") {
      return;
    }

    if (this.pageSizeOptions.includes(pageSize)) {
      window.localStorage.setItem(this.pageSizeStorageKey, String(pageSize));
    }
  }

  populateDonorFlags() {
    this.donorMetricFlags = {};

    this.filteredDonors.forEach((donor) => {
      const redList: string[] = [];
      const yellowList: string[] = [];
      const logic = environment.ineligibility_logic;

      const dob = donor.metrics?.find(
        (m: any) => m.metric_key === "DonorDOB"
      )?.metric_value;
      const age = parseInt(calculateAge(dob));

      const bmi = donor.metrics?.find(
        (m: any) => m.metric_key === "BMI"
      )?.metric_value;

      const EligibilityStatus = donor.metrics?.find(
        (m: any) => m.metric_key === "EligibilityStatus"
      )?.metric_value;

      this.contentService.evaluateMetric(
        age,
        logic.age,
        "age",
        redList,
        yellowList
      );
      this.contentService.evaluateMetric(
        bmi,
        logic.bmi,
        "BMI",
        redList,
        yellowList
      );
      this.contentService.evaluateMetric(
        EligibilityStatus,
        logic["Eligibility Status"],
        "Eligibility Status",
        redList,
        yellowList
      );

      this.donorMetricFlags[donor.email] = { red: redList, yellow: yellowList };
    });
  }

  getMetricColorClass(donor: any, field: string): string {
    const flags = this.donorMetricFlags[donor.email];
    if (!flags) return "";
    if (flags.red.map((f) => f.toLowerCase()).includes(field.toLowerCase()))
      return "background-light-red";
    if (flags.yellow.map((f) => f.toLowerCase()).includes(field.toLowerCase()))
      return "background-light-yellow";
    return "";
  }

  getRecommendationColorClass(donor: any, field: string): string {
    // Check if recommendations are available and this donor has recommendation data
    if (
      !this.isSortByRecommendationApplied ||
      !this.recommendationScores.has(donor.email)
    ) {
      return "";
    }

    // Get the recommendation data for this donor
    const donorRecommendations = this.getDonorRecommendations(donor.email);
    if (!donorRecommendations) {
      return "";
    }

    // Check if the field is in recommended metrics (highlight green)
    if (donorRecommendations.recommendedMetrics.includes(field)) {
      return "background-light-green";
    }

    // Check if the field is in not recommended metrics (highlight red)
    if (donorRecommendations.notRecommendedMetrics.includes(field)) {
      return "background-light-red";
    }

    return "";
  }

  private getDonorRecommendations(donorEmail: string): any | null {
    // This would need to be implemented based on how you store the recommendation data
    // For now, we'll need to store the full recommendation data when it's fetched
    return this.donorRecommendations?.get(donorEmail) || null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["donors"]) {
      this.filteredDonors = [...this.donors];
      this.sortByStepsCompleted(); // Apply default sort when donors data changes
      this.updatePagination();
    }

    if (changes["activeCardFilter"]) {
      const newValue = changes["activeCardFilter"].currentValue;
      if (!newValue) {
        this.clearFilters();
      } else {
        this.handleCardFilter(newValue);

        // add status filter value to the list of filters
        this.updateStatusFilterValues();
      }
    }
  }

  ngAfterViewInit() {
    this.checkIfScrollable();
    window.addEventListener("resize", this.handleWindowResize);

    const el = this.scrollWrapper?.nativeElement;
    if (el && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.checkIfScrollable());
      this.resizeObserver.observe(el);
    }
  }

  ngOnDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    this.resizeObserver?.disconnect();
  }

  checkIfScrollable() {
    setTimeout(() => {
      const el = this.scrollWrapper?.nativeElement;
      if (el) {
        this.isContentScrollable =
          el.scrollWidth > el.clientWidth &&
          el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
      }
    }, 50); // Slight delay to ensure DOM updates are reflected
  }

  private refreshTableLayout(resetScroll = false): void {
    setTimeout(() => {
      const el = this.scrollWrapper?.nativeElement;
      if (resetScroll && el) {
        el.scrollLeft = 0;
      }

      this.checkIfScrollable();
    });
  }

  updateStatusFilterValues() {
    const statusFilter = this.availableFilters.find((f) => f.key === "status");
    const donorStatuses = this.donors
      .map((d) => d.dashboardStatus)
      .filter(Boolean);
    const activeStatusFilters = this.activeFilters["status"] || [];
    statusFilter!.options = Array.from(
      new Set([...donorStatuses, ...activeStatusFilters])
    ).sort((a, b) => a.localeCompare(b));
  }

  handleCardFilter(filterKey: string | null) {
    if (!filterKey) return;

    // Clear existing dashboardStatus filter
    this.filters["status"].clear();
    this.activeFilters["status"] = [];

    // Apply the filter to dashboardStatus only
    this.filters["status"].add(filterKey);
    this.activeFilters["status"] = [filterKey];

    // Reapply with updated filters
    this.applyFilters();
  }

  getAvailableDonorAgeBuckets(): string[] {
    const ageBuckets = new Set<string>();
    this.donors.forEach((donor) => {
      const dob = donor.metrics?.find(
        (m: any) => m.metric_key === "DonorDOB"
      )?.metric_value;

      if (dob) {
        const age = calculateAge(dob);
        const bucket = this.getAgeBucket(age);
        if (bucket) ageBuckets.add(bucket);
      }
    });
    return Array.from(ageBuckets).sort((a, b) => a.localeCompare(b));
  }

  getAvailableBMIBuckets(): string[] {
    const bmiBuckets = new Set<string>();
    this.donors.forEach((donor) => {
      const bmi = donor.metrics?.find(
        (m: any) => m.metric_key === "BMI"
      )?.metric_value;
      if (bmi) {
        const bucket = this.getBMIBucket(bmi);
        if (bucket) bmiBuckets.add(bucket);
      }
    });
    // Explicit order for BMI buckets
    const order = [
      "Underweight (BMI < 18.5)",
      "Normal (BMI ≥ 18.5 and < 25)",
      "Overweight (BMI ≥ 25 and < 32)",
      "Obese (BMI ≥ 32)",
    ];
    const buckets = Array.from(bmiBuckets);
    // Sort buckets according to desired order
    return order.filter((label) => buckets.includes(label));
  }

  getAvailableBSABuckets(): string[] {
    const bsaBuckets = new Set<string>();
    this.donors.forEach((donor) => {
      const bsa = donor.metrics?.find(
        (m: any) => m.metric_key === "BSA"
      )?.metric_value;
      if (bsa) {
        const bucket = this.getBSABucket(bsa);
        if (bucket) bsaBuckets.add(bucket);
      }
    });
    // Explicit order for BSA buckets
    const order = [
      "Very Low (BSA < 1.3 m²)",
      "Low (BSA ≥ 1.3 and < 1.6 m²)",
      "Normal (BSA ≥ 1.6 and < 1.9 m²)",
      "High (BSA ≥ 1.9 and < 2.2 m²)",
      "Very High (BSA ≥ 2.2 m²)",
    ];
    const buckets = Array.from(bsaBuckets);
    // Sort buckets according to desired order
    return order.filter((label) => buckets.includes(label));
  }

  searchDonors() {
    this.applyFilters();

    const keywords = this.searchQuery.toLowerCase().trim().split(/\s+/);

    this.filteredDonors = this.filteredDonors.filter((donor) => {
      const fullName = `${donor.firstName?.trim()} ${donor.lastName?.trim()}`;
      const email = donor.email?.toLowerCase() || "";
      const assignedTo = donor.assignedTo?.toLowerCase() || "";
      const recipientName =
        this.getRecipientNameMetric(donor.metrics)?.toLowerCase() || "";

      const expandedMetricContent = this.expandedMetricKeys
        .map((key) => this.getMetricValue(donor.metrics, key))
        .join(" ")
        .toLowerCase();

      const dashboardStatus = donor.dashboardStatus?.toLowerCase() || "";

      const contentToSearch =
        fullName +
        " " +
        donor.firstName?.toLowerCase() +
        " " +
        donor.lastName?.toLowerCase() +
        " " +
        email +
        " " +
        assignedTo +
        " " +
        recipientName +
        " " +
        dashboardStatus +
        " " +
        expandedMetricContent;

      return keywords.every((kw) => contentToSearch.includes(kw));
    });

    // Apply default sort by steps completed (high to low) after search filtering
    this.sortByStepsCompleted();
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByStepsCompleted() {
    // Toggle sort direction if already sorted by this field, otherwise default to desc
    if (this.sortedField === "stepsCompleted") {
      this.progressSortDirection =
        this.progressSortDirection === "asc" ? "desc" : "asc";
    } else {
      this.progressSortDirection = "desc"; // Default to high to low
    }

    this.sortedField = "stepsCompleted";

    this.filteredDonors.sort((a, b) => {
      const completedA = parseInt(a.completedSections) || 0;
      const completedB = parseInt(b.completedSections) || 0;
      return this.progressSortDirection === "asc"
        ? completedA - completedB
        : completedB - completedA;
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByProgress() {
    this.sortedField = "progress";
    this.progressSortDirection =
      this.progressSortDirection === "asc" ? "desc" : "asc";
    this.filteredDonors.sort((a, b) => {
      const progressA = parseFloat(a.progress) || 0;
      const progressB = parseFloat(b.progress) || 0;
      return this.progressSortDirection === "asc"
        ? progressA - progressB
        : progressB - progressA;
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByStatus() {
    this.sortedField = "status";
    this.statusSortDirection =
      this.statusSortDirection === "asc" ? "desc" : "asc";
    this.filteredDonors.sort((a, b) => {
      const progressA = parseFloat(a.progress) || 0;
      const progressB = parseFloat(b.progress) || 0;
      return this.statusSortDirection === "asc"
        ? progressA - progressB
        : progressB - progressA;
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByAssignedTo() {
    this.sortedField = "assignedTo";
    this.assignedSortDirection =
      this.assignedSortDirection === "asc" ? "desc" : "asc";
    this.filteredDonors.sort((a, b) => {
      const assignedA = a.assignedTo || "";
      const assignedB = b.assignedTo || "";
      return this.assignedSortDirection === "asc"
        ? assignedA.localeCompare(assignedB)
        : assignedB.localeCompare(assignedA);
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByDonor() {
    this.sortedField = "donor";
    this.donorSortDirection =
      this.donorSortDirection === "asc" ? "desc" : "asc";
    this.filteredDonors.sort((a, b) => {
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`
        .trim()
        .toLowerCase();
      return this.donorSortDirection === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  sortByRecipient() {
    this.sortedField = "recipient";
    this.recipientSortDirection =
      this.recipientSortDirection === "asc" ? "desc" : "asc";
    this.filteredDonors.sort((a, b) => {
      const recipientA =
        this.getRecipientNameMetric(a.metrics)?.toLowerCase() || "";
      const recipientB =
        this.getRecipientNameMetric(b.metrics)?.toLowerCase() || "";
      return this.recipientSortDirection === "asc"
        ? recipientA.localeCompare(recipientB)
        : recipientB.localeCompare(recipientA);
    });
    this.updatePagination();
    this.currentPage = 1;
  }

  getFilteredFilters(): any[] {
    if (!this.filterSearch) return this.availableFilters;
    const lowerSearch = this.filterSearch.toLowerCase();
    return this.availableFilters.filter((f) =>
      f.label.toLowerCase().includes(lowerSearch)
    );
  }

  getFilterLabel(key: string): string {
    return this.availableFilters.find((f) => f.key === key)?.label || "";
  }

  getSubFilterOptions(key: string): string[] {
    return this.availableFilters.find((f) => f.key === key)?.options ?? [];
  }

  setActiveSubFilter(filterKey: string, elementRef: HTMLElement) {
    this.activeSubFilter = filterKey;
    const parentTop =
      (
        elementRef.closest(".filter-dropdown-menu") as HTMLElement
      )?.getBoundingClientRect().top ?? 0;
    const itemTop = elementRef.getBoundingClientRect().top;

    this.subFilterTopOffset = itemTop - parentTop + 46;
  }

  toggleFilterOption(filterKey: string, option: string): void {
    const currentSet = this.filters[filterKey];
    if (currentSet.has(option)) {
      currentSet.delete(option);
    } else {
      currentSet.add(option);
    }

    // Sync activeFilters as well for UI highlighting
    this.activeFilters[filterKey] = Array.from(currentSet);
  }

  isSelectedFilterOption(filterKey: string, option: string): boolean {
    return this.activeFilters[filterKey]?.includes(option);
  }

  hasSelectedFilterOption(filterKey: string): boolean {
    return (
      this.activeFilters[filterKey] &&
      Array.isArray(this.activeFilters[filterKey]) &&
      this.activeFilters[filterKey].length > 0
    );
  }

  hasAnyFiltersApplied(): boolean {
    return Object.values(this.activeFilters).some(
      (options) => Array.isArray(options) && options.length > 0
    );
  }

  resetFilter(filterKey: string): void {
    this.filters[filterKey].clear();
    this.activeFilters[filterKey] = [];
  }

  onDropdownClose(isOpen: boolean) {
    if (!isOpen) {
      this.activeSubFilter = null;
    }
  }

  isRecipientWithRecommendationsSelected() {
    if (this.selectedRecipient && this.filteredDonors.length > 0) {
      const donor = this.filteredDonors[0];
      const recipientName = this.getRecipientNameMetric(donor.metrics);
      const nameParts = recipientName.trim().split(/\s+/).filter(Boolean);
      const recipientFirstName = nameParts[0] || "";
      const recipientLastName = nameParts.slice(1).join(" ").trim();
      const recipientDOB = donor.metrics?.find(
        (m: any) => m.metric_key === "RecipientDOB"
      )?.metric_value;

      if (donor && recipientFirstName && recipientLastName && recipientDOB) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  // Helper: Extract DOB from filter option string (e.g., "Jones Davis <span class=\"dob-text\">DOB: 12/08/1988</span>")
  extractDOBFromOption(option: string): string | null {
    const match = option.match(
      /<span[^>]*class=["']dob-text["'][^>]*>DOB:\s*([^<]+)<\/span>/i
    );
    if (match && match[1]) {
      const rawDate = match[1].trim();
      if (rawDate === "Unknown") return "Unknown";

      // Expect MM/DD/YYYY
      const parts = rawDate.split("/");
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }

      return rawDate;
    }
    return null;
  }

  applyFilters(): void {
    this.activeSubFilter = null;

    this.selectedRecipient =
      this.activeFilters["recipients"] &&
      this.activeFilters["recipients"].length === 1;

    const recipientFilterSet = this.filters["recipients"];
    const recipientFilterArray = Array.from(recipientFilterSet);

    this.filteredDonors = this.donors.filter((donor) => {
      // Compose composite recipient key for filtering
      const recipientName = this.getRecipientNameMetric(donor.metrics);
      const recipientDOB = donor.metrics?.find(
        (m: any) => m.metric_key === "RecipientDOB"
      )?.metric_value;

      const status = donor.dashboardStatus;
      const assignedTo = donor.assignedTo || "Unassigned";

      const metricMap: Record<string, string> = {};
      donor.metrics?.forEach((m: any) => {
        if (m.metric_key && m.metric_value) {
          metricMap[m.metric_key] = m.metric_value;
        }
      });

      // Compute donor age bucket using DonorDOB
      const donorDOB = metricMap["DonorDOB"];
      const donorAgeBucket = donorDOB
        ? this.getAgeBucket(calculateAge(donorDOB))
        : "";

      const bmiBucket = this.getBMIBucket(metricMap["BMI"]);
      const bsaBucket = this.getBSABucket(metricMap["BSA"]);
      const gender = metricMap["Gender"];
      const bloodtype = metricMap["BloodType"];
      const relationship = metricMap["Relationship"];
      const eligibilityStatus = metricMap["EligibilityStatus"];

      // Recipient filter logic
      let recipientMatches: boolean;
      if (!recipientFilterSet.size) {
        recipientMatches = true;
      } else {
        recipientMatches = recipientFilterArray.some((option) => {
          const namePart = option.split("<span")[0].trim();
          const dobText = this.extractDOBFromOption(option);

          const normalizedOptionName = this.normalizeRecipientName(namePart);
          const normalizedDonorName = this.normalizeRecipientName(recipientName);
          const normalizedDonorDOB = this.normalizeRecipientDob(recipientDOB);

          if (dobText === "Unknown") {
            return (
              normalizedOptionName === normalizedDonorName &&
              !normalizedDonorDOB
            );
          }

          return (
            normalizedOptionName === normalizedDonorName &&
            !!normalizedDonorDOB &&
            dobText === normalizedDonorDOB
          );
        });
      }

      const statusMatches =
        !this.filters["status"].size || this.filters["status"].has(status);

      const assignedToMatches =
        !this.filters["assignedTo"].size ||
        this.filters["assignedTo"].has(assignedTo);

      const ageMatches =
        !this.filters["age"].size || this.filters["age"].has(donorAgeBucket);

      const bmiMatches =
        !this.filters["bmi"].size || this.filters["bmi"].has(bmiBucket);

      const bsaMatches =
        !this.filters["bsa"].size || this.filters["bsa"].has(bsaBucket);

      const genderMatches =
        !this.filters["gender"].size || this.filters["gender"].has(gender);

      const bloodTypeMatches =
        !this.filters["bloodtype"].size ||
        this.filters["bloodtype"].has(bloodtype);

      const relationshipMatches =
        !this.filters["relationship"].size ||
        this.filters["relationship"].has(relationship);

      const eligibilityMatches =
        !this.filters["eligibilityStatus"].size ||
        (eligibilityStatus &&
          this.filters["eligibilityStatus"].has(eligibilityStatus));

      return (
        recipientMatches &&
        statusMatches &&
        assignedToMatches &&
        ageMatches &&
        bmiMatches &&
        bsaMatches &&
        genderMatches &&
        bloodTypeMatches &&
        relationshipMatches &&
        eligibilityMatches
      );
    });

    // Apply default sort by steps completed (high to low) after filtering
    this.sortByStepsCompleted();
    this.updatePagination();
    this.currentPage = 1;
  }

  clearFilters() {
    this.isSortByRecommendationApplied = false;
    this.recommendationScores.clear();
    this.donorRecommendations.clear();
    Object.keys(this.filters).forEach((key) => {
      this.filters[key].clear();
      this.activeFilters[key] = [];
    });

    // Reset the filteredDonors to the original full list
    this.filteredDonors = [...this.donors];

    // Apply default sort by steps completed (high to low) after clearing filters
    this.sortByStepsCompleted();

    // Close any open subfilter menus
    this.activeSubFilter = null;

    // Reset activeCardFilter and emit change event
    this.selectedRecipient = false;

    this.activeCardFilter = null;
    this.clearFiltersEvent.emit(null);
  }

  getProgressColor(progress: string): string {
    const progressValue = parseFloat(progress);

    if (progressValue < 30) {
      return "progress-low";
    } else if (progressValue < 100) {
      return "progress-medium";
    } else {
      return "progress-high";
    }
  }

  navigateToDonorJourney(donor: any) {
    if (donor?.email) {
      this.router.navigate([`hospital/donor/${donor.email}/synopsis`]);
    }
  }

  toggleDropdown(donor: any) {
    this.donors.forEach((d) => (d.showDropdown = false)); // Close all other dropdowns
    donor.showDropdown = !donor.showDropdown;
  }

  assignTo(donor: any, admin: any) {
    const previousAssignedTo = donor.assignedTo; // Store previous value in case of error
    donor.assignedTo = "Updating...";

    // Call API to update in backend
    this.contentService
      .updateDonorAssignment(donor.email, admin.email)
      .subscribe({
        next: () => {
          donor.assignedTo = admin.email;
          const assignedFilter = this.availableFilters.find(
            (f) => f.key === "assignedTo"
          );
          if (assignedFilter && !assignedFilter.options.includes(admin.email)) {
            assignedFilter.options.push(admin.email);
            assignedFilter.options = assignedFilter.options
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b));
          }
        },
        error: (error) => {
          console.error("Error updating assigned coordinator:", error);
          donor.assignedTo = previousAssignedTo;
        },
        complete: () => {
          donor.showDropdown = false; // Ensure dropdown closes after update
        },
      });
  }

  getAssignedToDisplayName(assignedTo?: string | null): string {
    if (!assignedTo) {
      return "Unassigned";
    }

    if (assignedTo === "Updating...") {
      return assignedTo;
    }

    const matchedAdmin = this.admins.find((admin) => admin.email === assignedTo);
    if (matchedAdmin) {
      const fullName = `${matchedAdmin.firstName || ""} ${matchedAdmin.lastName || ""}`.trim();
      return fullName.length > 0 ? fullName : assignedTo;
    }

    return assignedTo;
  }

  private ensureMetricsForAllDonors() {
    if (!this.useBulkMetrics) {
      return;
    }
    if (this.isBulkMetricsLoading) {
      return;
    }
    const donorsToLoad = this.donors.filter((donor) => {
      const email = donor?.email;
      if (!email) return false;
      if (this.loadingMetrics.has(email)) return false;
      if (Array.isArray(donor.metrics) && donor.metrics.length > 0) return false;
      const cachedMetrics = this.metricsCache.get(email);
      if (cachedMetrics && cachedMetrics.length > 0) {
        donor.metrics = cachedMetrics;
        return false;
      }
      return true;
    });

    if (donorsToLoad.length === 0) {
      return;
    }

    const userIds = donorsToLoad.map((donor) => donor.email);
    userIds.forEach((userId) => this.loadingMetrics.add(userId));
    this.isBulkMetricsLoading = true;

    this.questionnaireService.getDonorMetricsBulk(userIds).pipe(
      catchError((err) => {
        console.warn("Failed to fetch bulk metrics for donors:", err);
        return of({});
      })
    ).subscribe({
      next: (response: Record<string, any[]>) => {
        userIds.forEach((userId) => {
          const metrics = response?.[userId] || [];
          this.metricsCache.set(userId, metrics || []);
          const targetDonor = this.donors.find((d) => d.email === userId);
          if (targetDonor) {
            targetDonor.metrics = metrics || [];
          }
        });
        this.populateDonorFlags();
        this.rebuildFilterOptions();
        this.applyFilters();
        this.updatePagination();
        this.translateSpanishMotivationMetrics();
      },
      complete: () => {
        userIds.forEach((userId) => this.loadingMetrics.delete(userId));
        this.isBulkMetricsLoading = false;
      },
      error: () => {
        userIds.forEach((userId) => this.loadingMetrics.delete(userId));
        this.isBulkMetricsLoading = false;
      },
    });
  }

  isMetricsLoading(donor: any): boolean {
    const email = donor?.email;
    return email ? this.loadingMetrics.has(email) : false;
  }

  getRecipientNameMetric(metrics: any[], includeDob?: boolean): string {
    if (!Array.isArray(metrics)) return "";

    const nameMetric = metrics.find((m) => m.metric_key === "RecipientName");
    const dobMetric = metrics.find((m) => m.metric_key === "RecipientDOB");

    const fullName = nameMetric?.metric_value?.trim() || "";
    const dob = dobMetric?.metric_value?.trim();

    return includeDob && dob ? `${fullName} (${dob})` : fullName;
  }

  getDisplayableMetrics(
    metrics: any[],
    donor?: any
  ): { key: string; value: string; rawKey?: string; translated?: boolean }[] {
    const allowedKeys = [
      "EligibilityStatus",
      "BMI",
      "BSA",
      "BloodType",
      "Gender",
      "Relationship",
      "DonorMotivationLevel",
    ];

    if (!Array.isArray(metrics)) return [];

    const labelMap: Record<string, string> = {
      EligibilityStatus: "Eligibility Status",
      BMI: "BMI",
      BSA: "BSA",
      BloodType: "Blood Type",
      Gender: "Gender",
      Relationship: "Relationship",
      DonorMotivationLevel: "Motivation Level",
    };

    const displayMetrics = metrics
      .filter((m) => allowedKeys.includes(m.metric_key))
      .map((m) => ({
        key: labelMap[m.metric_key] || m.metric_key,
        value:
          m.metric_key === "DonorMotivationLevel" && donor
            ? this.getMotivationDisplayValue(donor, m.metric_value)
            : m.metric_value,
        rawKey: m.metric_key,
        translated:
          m.metric_key === "DonorMotivationLevel" &&
          donor &&
          this.isMotivationTranslated(donor, m.metric_value),
      }));

    const donorDOB = metrics.find(
      (m) => m.metric_key === "DonorDOB"
    )?.metric_value;
    const recipientDOB = metrics.find(
      (m) => m.metric_key === "RecipientDOB"
    )?.metric_value;

    const ageMetrics: { key: string; value: string }[] = [];

    if (donorDOB) {
      ageMetrics.push({ key: "Age", value: calculateAge(donorDOB) });
    }

    if (recipientDOB) {
      ageMetrics.push({
        key: "Recipient Age",
        value: calculateAge(recipientDOB),
      });
    }

    return [...ageMetrics, ...displayMetrics];
  }

  private translateSpanishMotivationMetrics(): void {
    this.donors.forEach((donor) => {
      if (!this.isSpanishDonor(donor) || !Array.isArray(donor.metrics)) {
        return;
      }

      const motivationValue = donor.metrics.find(
        (metric: any) => metric.metric_key === "DonorMotivationLevel"
      )?.metric_value;

      if (!motivationValue) {
        return;
      }

      const cacheKey = this.getMotivationTranslationKey(donor, motivationValue);

      if (
        this.motivationTranslationCache.has(cacheKey) ||
        this.loadingMotivationTranslations.has(cacheKey)
      ) {
        return;
      }

      this.loadingMotivationTranslations.add(cacheKey);
      this.contentService
        .translateText({
          sourceLocale: "es",
          targetLocale: "en",
          text: this.getMotivationExplanation(motivationValue),
          context:
            "Living kidney donor initial questionnaire motivation level answer",
        })
        .pipe(
          catchError((error) => {
            console.error("Error translating motivation level:", error);
            return of(null);
          })
        )
        .subscribe({
          next: (translation) => {
            const translatedText = translation?.translatedText?.trim();

            if (translatedText) {
              this.motivationTranslationCache.set(
                cacheKey,
                this.replaceMotivationExplanation(
                  motivationValue,
                  translatedText
                )
              );
            }
          },
          complete: () => {
            this.loadingMotivationTranslations.delete(cacheKey);
          },
        });
    });
  }

  getMotivationDisplayValue(donor: any, value: string): string {
    return (
      this.motivationTranslationCache.get(
        this.getMotivationTranslationKey(donor, value)
      ) || value
    );
  }

  isMotivationTranslated(donor: any, value: string): boolean {
    return this.motivationTranslationCache.has(
      this.getMotivationTranslationKey(donor, value)
    );
  }

  isSpanishDonor(donor: any): boolean {
    return (
      this.getDonorLocale(donor).startsWith("es") ||
      this.hasSpanishMotivationText(donor)
    );
  }

  private getDonorLocale(donor: any): string {
    const locale =
      donor?.locale ||
      donor?.preferredLocale ||
      donor?.donorLocale ||
      donor?.language ||
      donor?.metrics?.find((metric: any) =>
        ["DonorLocale", "Locale", "PreferredLocale", "Language"].includes(
          metric.metric_key
        )
      )?.metric_value ||
      "";

    return String(locale).trim().toLowerCase();
  }

  private getMotivationTranslationKey(donor: any, value: string): string {
    return `${donor?.email || donor?.userId || ""}|${String(value || "").trim()}`;
  }

  private getMotivationExplanation(value: string): string {
    const parts = String(value || "").split(/\s+-\s+/);
    return (parts.length > 1 ? parts.slice(1).join(" - ") : value).trim();
  }

  private replaceMotivationExplanation(
    originalValue: string,
    translatedExplanation: string
  ): string {
    const parts = String(originalValue || "").split(/\s+-\s+/);

    if (parts.length <= 1) {
      return translatedExplanation;
    }

    return `${parts[0].trim()} - ${translatedExplanation}`;
  }

  private hasSpanishMotivationText(donor: any): boolean {
    const motivationValue = donor?.metrics?.find(
      (metric: any) => metric.metric_key === "DonorMotivationLevel"
    )?.metric_value;

    if (!motivationValue) {
      return false;
    }

    const explanation = this.normalizeText(
      this.getMotivationExplanation(motivationValue)
    );

    return [
      "muy ",
      " motivado",
      " motivada",
      " donar",
      " hermano",
      " hermana",
      " padre",
      " madre",
      " hijo",
      " hija",
      " para ",
      " porque ",
    ].some((signal) => explanation.includes(signal.trim()));
  }

  private normalizeText(value: string): string {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  getAgeBucket(age: string): string {
    const value = parseInt(age, 10);
    if (isNaN(value)) return "";
    if (value <= 18) return "0–18";
    if (value <= 29) return "19–29";
    if (value <= 39) return "30–39";
    if (value <= 49) return "40–49";
    if (value <= 64) return "50–64";
    return "65+";
  }

  getBMIBucket(bmi: string): string {
    const value = parseFloat(bmi);
    if (isNaN(value)) return "";
    if (value < 18.5) return "Underweight (BMI < 18.5)";
    if (value < 25) return "Normal (BMI ≥ 18.5 and < 25)";
    if (value < 32) return "Overweight (BMI ≥ 25 and < 32)";
    return "Obese (BMI ≥ 32)";
  }

  getBSABucket(bsa: string): string {
    const value = parseFloat(bsa);
    if (isNaN(value)) return "";
    if (value < 1.3) return "Very Low (BSA < 1.3 m²)";
    if (value < 1.6) return "Low (BSA ≥ 1.3 and < 1.6 m²)";
    if (value < 1.9) return "Normal (BSA ≥ 1.6 and < 1.9 m²)";
    if (value < 2.2) return "High (BSA ≥ 1.9 and < 2.2 m²)";
    return "Very High (BSA ≥ 2.2 m²)";
  }
  get expandedMetricKeys(): string[] {
    return [
      "Age",
      "Recipient Age",
      "Blood Type",
      "BMI",
      "BSA",
      "Relationship",
      "Eligibility Status",
      "Gender",
      "Motivation Level",
    ];
  }

  // Retrieve a specific metric value by key for expanded metrics
  getMetricValue(metrics: any[], key: string): string {
    if (!Array.isArray(metrics)) return "";

    if (key === "Age") {
      const dob = metrics.find(
        (m) => m.metric_key === "DonorDOB"
      )?.metric_value;
      return dob ? calculateAge(dob) : "";
    }

    if (key === "Recipient Age") {
      const dob = metrics.find(
        (m) => m.metric_key === "RecipientDOB"
      )?.metric_value;
      return dob ? calculateAge(dob) : "";
    }

    const labelMap: Record<string, string> = {
      "Blood Type": "BloodType",
      BMI: "BMI",
      BSA: "BSA",
      Relationship: "Relationship",
      "Eligibility Status": "EligibilityStatus",
      Gender: "Gender",
      "Motivation Level": "DonorMotivationLevel",
    };
    const match = metrics.find((m) => {
      return m.metric_key === labelMap[key];
    });

    return match?.metric_value ?? "";
  }

  /**
   * Sorts the filteredDonors list by a selected expanded metric column.
   * @param key The expanded metric key to sort by.
   */
  sortByExpandedMetric(key: string) {
    this.sortedField = key;
    this.expandedSortDirection =
      this.expandedSortDirection === "asc" ? "desc" : "asc";

    this.filteredDonors.sort((a, b) => {
      const valueA = this.getMetricValue(a.metrics, key)?.toLowerCase() || "";
      const valueB = this.getMetricValue(b.metrics, key)?.toLowerCase() || "";

      const numA = parseFloat(valueA);
      const numB = parseFloat(valueB);

      if (!isNaN(numA) && !isNaN(numB)) {
        return this.expandedSortDirection === "asc" ? numA - numB : numB - numA;
      }

      return this.expandedSortDirection === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
  }

  sortByBestMatch() {
    this.isSortByRecommendationApplied = !this.isSortByRecommendationApplied;

    if (this.isSortByRecommendationApplied) {
      this.isLoadingRecommendations = true;

      const donor = this.filteredDonors[0];
      if (donor) {
        const recipientName = this.getRecipientNameMetric(donor.metrics);
        const nameParts = recipientName.trim().split(/\s+/).filter(Boolean);
        const recipientFirstName = nameParts[0] || "";
        const recipientLastName = nameParts.slice(1).join(" ").trim();
        const recipientDOB = donor.metrics?.find(
          (m: any) => m.metric_key === "RecipientDOB"
        )?.metric_value;

        if (recipientFirstName && recipientLastName && recipientDOB) {
          this.contentService
            .getDonorRecommendations(
              recipientFirstName,
              recipientLastName,
              recipientDOB
            )
            .subscribe({
              next: (recommendations) => {
                const scoreMap = new Map<string, number>();
                const recommendationsMap = new Map<string, any>();
                recommendations.forEach((r: any) => {
                  scoreMap.set(r.donorId, r.score);
                  recommendationsMap.set(r.donorId, r);
                });

                this.recommendationScores = scoreMap;
                this.donorRecommendations = recommendationsMap;

                this.filteredDonors.sort((a, b) => {
                  const scoreA = scoreMap.get(a.email) ?? -Infinity;
                  const scoreB = scoreMap.get(b.email) ?? -Infinity;
                  return scoreB - scoreA;
                });

                this.isLoadingRecommendations = false;
              },
              error: (err) => {
                console.error("Error fetching recommendations:", err);
                this.isLoadingRecommendations = false;
              },
            });
        } else {
          this.isLoadingRecommendations = false;
        }
      } else {
        this.isLoadingRecommendations = false;
      }
    }
  }

  private refreshDonorsList() {
    this.isLoadingDonors = true;

    this.dashboardService
      .getAllDonors()
      .subscribe({
        next: (donors: any[]) => {
          this.donors = donors || [];
          // Rebuild options/filters, re-apply filters & sorts, update pagination, flags, etc.
          this.initializeFromDonors();
        },
        error: (err) => {
          console.error("Failed to refresh donors list:", err);
          // fall back to empty to keep UI consistent
          this.donors = [];
          this.initializeFromDonors();
        },
        complete: () => {
          this.isLoadingDonors = false;
        },
      });
  }
  openDonorProfileAdminActions(donor: any) {
    const modalRef = this.modalService.open(UserProfileModalComponent, {
      centered: true,
      backdrop: "static",
    });

    modalRef.componentInstance.user = { userRole: "Donor", ...donor };

    // When the modal closes with an action, refresh the donors list
    modalRef.result
      .then((result: any) => {
        if (result) {
          this.refreshDonorsList();
        }
      })
      .catch(() => {
        /* dismissed – no action */
      });
  }

  scrollRight() {
    const el = this.scrollWrapper?.nativeElement;
    if (el) {
      el.scrollBy({
        left: el.clientWidth,
        behavior: "smooth",
      });
    }
  }

  /** Build recipient dropdown options (Name + DOB/Unknown), unique and sorted */
  private buildRecipientOptions(): string[] {
    const recipientSet = new Set<string>();
    const options: string[] = [];

    this.donors.forEach((donor) => {
      const name = this.getRecipientNameMetric(donor.metrics);
      const dob = donor.metrics?.find(
        (m: any) => m.metric_key === "RecipientDOB"
      )?.metric_value;
      if (!name) return;

      const normalizedName = this.normalizeRecipientName(name);
      const normalizedDob = this.normalizeRecipientDob(dob);
      const dedupeKey = `${normalizedName}|${normalizedDob || "unknown"}`;

      if (dob && dob.trim() !== "") {
        const formattedDOB = new Date(dob).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
        const option = `${name} <span class="dob-text">DOB: ${formattedDOB}</span>`;
        if (!recipientSet.has(dedupeKey)) {
          options.push(option);
          recipientSet.add(dedupeKey);
        }
      } else {
        const option = `${name} <span class="dob-text">DOB: Unknown</span>`;
        if (!recipientSet.has(dedupeKey)) {
          options.push(option);
          recipientSet.add(dedupeKey);
        }
      }
    });

    return options.sort((a, b) => a.localeCompare(b));
  }

  private normalizeRecipientName(name: string): string {
    return name
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  private normalizeRecipientDob(dob?: string): string | null {
    if (!dob) return null;
    const trimmed = dob.trim();
    if (!trimmed) return null;

    const parts = trimmed.split("/");
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    const asDate = new Date(trimmed);
    if (Number.isNaN(asDate.getTime())) {
      return trimmed;
    }

    const year = asDate.getFullYear();
    const month = String(asDate.getMonth() + 1).padStart(2, "0");
    const day = String(asDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /** Ensure a filter exists in availableFilters and set options */
  private upsertFilter(key: string, label: string, options: string[]) {
    const existing = this.availableFilters.find((f) => f.key === key);
    if (existing) {
      existing.label = label;
      existing.options = options;
    } else {
      this.availableFilters.push({ key, label, options });
    }
  }

  /** Keep activeFilters in sync with current available options (drop invalid selections). */
  private syncActiveFiltersWithOptions() {
    this.availableFilters.forEach((f) => {
      const allowed = new Set(f.options || []);
      const active = this.activeFilters[f.key] || [];
      this.activeFilters[f.key] = active.filter((v) => allowed.has(v));
    });
  }

  /** Rebuild all dynamic filter options based on current donors */
  private rebuildFilterOptions() {
    // Recipients (Name + DOB)
    this.upsertFilter("recipients", "Recipients", this.buildRecipientOptions());

    // Status
    const statusOptions = Array.from(
      new Set(this.donors.map((d) => d.dashboardStatus).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    this.upsertFilter("status", "Status", statusOptions);

    // Assigned To
    const assignedOptions = Array.from(
      new Set(
        this.donors.map((d) => (d.assignedTo ? d.assignedTo : "Unassigned"))
      )
    ).sort((a, b) => a.localeCompare(b));
    this.upsertFilter("assignedTo", "Assigned To", assignedOptions);

    // Helper to get metric options
    const getMetricOptions = (metricKey: string): string[] => {
      return Array.from(
        new Set(
          this.donors
            .map(
              (d) =>
                d.metrics?.find((m: any) => m.metric_key === metricKey)
                  ?.metric_value
            )
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
    };

    // Computed bucket options
    const ageOptions = this.getAvailableDonorAgeBuckets();
    const bmiOptions = this.getAvailableBMIBuckets();
    const bsaOptions = this.getAvailableBSABuckets();

    this.upsertFilter("age", "Age", ageOptions);
    this.upsertFilter("bloodtype", "Blood Type", getMetricOptions("BloodType"));
    this.upsertFilter("bmi", "BMI", bmiOptions);
    this.upsertFilter("bsa", "BSA", bsaOptions);
    this.upsertFilter(
      "eligibilityStatus",
      "Eligibility Status",
      getMetricOptions("EligibilityStatus")
    );
    this.upsertFilter("gender", "Gender", getMetricOptions("Gender"));
    this.upsertFilter(
      "relationship",
      "Relationship",
      getMetricOptions("Relationship")
    );

    // Ensure backing Sets exist for every available filter
    this.availableFilters.forEach((f) => {
      if (!this.filters[f.key]) this.filters[f.key] = new Set<string>();
      if (!this.activeFilters[f.key]) this.activeFilters[f.key] = [];
    });

    // Apply any incoming filters from navigation once options are available
    this.applyPendingIncomingFilters();

    // Drop any active selections that are no longer valid after rebuild
    this.syncActiveFiltersWithOptions();
  }

  private applyPendingIncomingFilters(): void {
    if (this.incomingFiltersApplied) return;
    if (!this.pendingIncomingFilters || Object.keys(this.pendingIncomingFilters).length === 0) {
      this.incomingFiltersApplied = true;
      return;
    }

    const remaining: Record<string, string[]> = {};

    for (const [rawKey, values] of Object.entries(this.pendingIncomingFilters)) {
      const key = this.normalizeFilterKey(rawKey);
      const options = this.getSubFilterOptions(key);
      if (!options || options.length === 0) {
        remaining[rawKey] = values;
        continue;
      }

      const mapped = this.mapIncomingValues(key, values, options);
      if (mapped.length === 0) {
        remaining[rawKey] = values;
        continue;
      }

      if (!this.filters[key]) this.filters[key] = new Set<string>();
      mapped.forEach((v) => this.filters[key].add(v));
      this.activeFilters[key] = Array.from(this.filters[key]);
    }

    this.pendingIncomingFilters = remaining;
    if (Object.keys(this.pendingIncomingFilters).length === 0) {
      this.incomingFiltersApplied = true;
    }
  }

  private normalizeFilterKey(rawKey: string): string {
    const key = (rawKey || "").trim();
    const lower = key.toLowerCase();
    if (lower === "bloodtype") return "bloodtype";
    if (lower === "eligibilitystatus") return "eligibilityStatus";
    if (lower === "assignedto") return "assignedTo";
    if (lower === "recipientname") return "recipients";
    if (lower === "recipients") return "recipients";
    if (lower === "relationship") return "relationship";
    if (lower === "gender") return "gender";
    if (lower === "bmi") return "bmi";
    if (lower === "age") return "age";
    if (lower === "status") return "status";
    return key;
  }

  private mapIncomingValues(
    key: string,
    values: string[],
    options: string[]
  ): string[] {
    const mapped: string[] = [];
    const optionSet = new Set(options);

    for (const rawValue of values || []) {
      const value = (rawValue || "").trim();
      if (!value) continue;

      if (optionSet.has(value)) {
        mapped.push(value);
        continue;
      }

      if (key === "bmi") {
        const bmi = this.mapBmiLabel(value);
        if (bmi && optionSet.has(bmi)) {
          mapped.push(bmi);
        }
        continue;
      }

      if (key === "age") {
        const age = this.mapAgeLabel(value);
        if (age && optionSet.has(age)) {
          mapped.push(age);
        }
        continue;
      }

      if (key === "recipients") {
        const recipients = this.mapRecipientLabels(value, options);
        recipients.forEach((r) => mapped.push(r));
        continue;
      }
    }

    return Array.from(new Set(mapped));
  }

  private mapBmiLabel(label: string): string | null {
    const normalized = label.toLowerCase().replace(/\s+/g, " ").trim();
    const bmiMap: Record<string, string> = {
      "bmi < 18.5": "Underweight (BMI < 18.5)",
      "underweight": "Underweight (BMI < 18.5)",
      "18.5 bmi < 25": "Normal (BMI ≥ 18.5 and < 25)",
      "18.5 <= bmi < 25": "Normal (BMI ≥ 18.5 and < 25)",
      "18.5 ≤ bmi < 25": "Normal (BMI ≥ 18.5 and < 25)",
      "normal": "Normal (BMI ≥ 18.5 and < 25)",
      "25 ≤ bmi < 32": "Overweight (BMI ≥ 25 and < 32)",
      "25 <= bmi < 32": "Overweight (BMI ≥ 25 and < 32)",
      "25 bmi < 32": "Overweight (BMI ≥ 25 and < 32)",
      "overweight": "Overweight (BMI ≥ 25 and < 32)",
      "bmi ≥ 32": "Obese (BMI ≥ 32)",
      "bmi >= 32": "Obese (BMI ≥ 32)",
      "obese": "Obese (BMI ≥ 32)",
    };
    return bmiMap[normalized] || null;
  }

  private mapAgeLabel(label: string): string | null {
    const normalized = label.replace(/\s+/g, "").toLowerCase();
    const rangeMatch = normalized.match(/^(\d+)[-–](\d+)$/);
    if (rangeMatch) {
      const start = rangeMatch[1];
      const end = rangeMatch[2];
      return `${start}–${end}`;
    }
    const plusMatch = normalized.match(/^(\d+)\+$/);
    if (plusMatch) {
      return `${plusMatch[1]}+`;
    }
    return null;
  }

  private mapRecipientLabels(value: string, options: string[]): string[] {
    const mapped: string[] = [];
    const namePart = value.split("<span")[0].trim();
    if (!namePart) return mapped;

    const incomingName = this.normalizeRecipientName(namePart);
    const incomingDobRaw = this.extractDOBFromOption(value);
    const incomingDob =
      incomingDobRaw && incomingDobRaw !== "Unknown"
        ? this.normalizeRecipientDob(incomingDobRaw)
        : null;
    const incomingDobUnknown = incomingDobRaw === "Unknown";

    for (const option of options) {
      const optionNamePart = option.split("<span")[0].trim();
      if (!optionNamePart) continue;
      const optionName = this.normalizeRecipientName(optionNamePart);
      if (optionName !== incomingName) continue;

      const optionDobRaw = this.extractDOBFromOption(option);
      const optionDob =
        optionDobRaw && optionDobRaw !== "Unknown"
          ? this.normalizeRecipientDob(optionDobRaw)
          : null;
      const optionDobUnknown = optionDobRaw === "Unknown";

      if (incomingDob) {
        if (optionDob && optionDob === incomingDob) {
          mapped.push(option);
        }
      } else if (incomingDobUnknown) {
        if (optionDobUnknown) {
          mapped.push(option);
        }
      } else {
        mapped.push(option);
      }
    }

    return mapped;
  }
}
